import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    IconButton,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Avatar,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    Badge,
    Divider,
    CircularProgress
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';

// icons
import {
    IconServer,
    IconActivity,
    IconDatabase,
    IconUsers,
    IconRefresh,
    IconDownload,
    IconAlertCircle,
    IconCheck,
    IconTrendingUp,
    IconTrendingDown,
    IconBolt,
    IconPlayerPlay,
    IconSettings,
    IconFileAnalytics
} from '@tabler/icons-react';

const ServerMonitoring = () => {
    const theme = useTheme();
    const [timeRange, setTimeRange] = useState('1h');
    const [selectedServer, setSelectedServer] = useState('all');
    const [refreshing, setRefreshing] = useState(false);

    // Mock server data
    const servers = [
        {
            id: 'server-1',
            name: 'jupyter-hub-01',
            status: 'healthy',
            uptime: '15d 8h 23m',
            users: 45,
            notebooks: 67,
            cpu: 42,
            memory: 65,
            disk: 78,
            network: '124 MB/s'
        },
        {
            id: 'server-2',
            name: 'jupyter-hub-02',
            status: 'warning',
            uptime: '8d 12h 45m',
            users: 38,
            notebooks: 52,
            cpu: 78,
            memory: 82,
            disk: 45,
            network: '98 MB/s'
        },
        {
            id: 'server-3',
            name: 'jupyter-worker-01',
            status: 'healthy',
            uptime: '23d 5h 12m',
            users: 12,
            notebooks: 23,
            cpu: 25,
            memory: 38,
            disk: 32,
            network: '45 MB/s'
        }
    ];

    // Performance metrics
    const metrics = {
        totalUsers: 95,
        activeNotebooks: 142,
        avgResponseTime: '124ms',
        requestsPerMin: 847,
        errorRate: '0.02%',
        avgCpu: 48,
        avgMemory: 61,
        totalStorage: '2.4 TB'
    };

    // Activity log
    const activityLog = [
        { time: '2 mins ago', user: 'john.doe', action: 'Started notebook', details: 'ML Model Training.ipynb', type: 'start' },
        { time: '5 mins ago', user: 'jane.smith', action: 'Kernel restarted', details: 'Data Analysis.ipynb', type: 'restart' },
        { time: '12 mins ago', user: 'admin', action: 'Server configuration updated', details: 'Increased memory limit', type: 'config' },
        { time: '18 mins ago', user: 'bob.johnson', action: 'Notebook saved', details: 'API Integration.ipynb', type: 'save' },
        { time: '25 mins ago', user: 'system', action: 'Auto-shutdown idle kernel', details: 'Kernel idle for 30 mins', type: 'system' }
    ];

    // System metrics chart
    const systemMetricsOptions = {
        chart: {
            type: 'area',
            height: 300,
            toolbar: { show: false },
            stacked: true
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 1 },
        fill: {
            type: 'gradient',
            gradient: {
                opacityFrom: 0.6,
                opacityTo: 0.1
            }
        },
        xaxis: {
            categories: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00'],
            labels: { style: { colors: theme.palette.text.secondary } }
        },
        yaxis: {
            title: { text: 'Usage %' },
            labels: { style: { colors: theme.palette.text.secondary } }
        },
        colors: [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.warning.main],
        legend: {
            position: 'top',
            horizontalAlign: 'right'
        }
    };

    const systemMetricsSeries = [
        {
            name: 'CPU',
            data: [30, 35, 42, 48, 55, 42]
        },
        {
            name: 'Memory',
            data: [45, 50, 58, 65, 70, 61]
        },
        {
            name: 'Disk I/O',
            data: [20, 25, 30, 35, 32, 28]
        }
    ];

    // User activity chart
    const userActivityOptions = {
        chart: {
            type: 'bar',
            height: 250,
            toolbar: { show: false }
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
                borderRadius: 4
            }
        },
        dataLabels: { enabled: false },
        xaxis: {
            categories: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
            labels: { style: { colors: theme.palette.text.secondary } }
        },
        yaxis: {
            title: { text: 'Active Users' },
            labels: { style: { colors: theme.palette.text.secondary } }
        },
        colors: [theme.palette.primary.main]
    };

    const userActivitySeries = [
        {
            name: 'Active Users',
            data: [85, 92, 88, 95, 91, 45, 32]
        }
    ];

    const handleRefresh = () => {
        setRefreshing(true);
        setTimeout(() => setRefreshing(false), 2000);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'healthy':
                return theme.palette.success.main;
            case 'warning':
                return theme.palette.warning.main;
            case 'error':
                return theme.palette.error.main;
            default:
                return theme.palette.grey[500];
        }
    };

    const getActionIcon = (type) => {
        switch (type) {
            case 'start':
                return <IconPlayerPlay size={16} />;
            case 'restart':
                return <IconRefresh size={16} />;
            case 'config':
                return <IconSettings size={16} />;
            case 'save':
                return <IconDatabase size={16} />;
            case 'system':
                return <IconBolt size={16} />;
            default:
                return <IconActivity size={16} />;
        }
    };

    return (
        <Box>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">Server Monitoring Dashboard</Typography>
                <Box display="flex" gap={1} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Time Range</InputLabel>
                        <Select value={timeRange} label="Time Range" onChange={(e) => setTimeRange(e.target.value)}>
                            <MenuItem value="1h">Last Hour</MenuItem>
                            <MenuItem value="6h">Last 6 Hours</MenuItem>
                            <MenuItem value="24h">Last 24 Hours</MenuItem>
                            <MenuItem value="7d">Last 7 Days</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="outlined"
                        startIcon={refreshing ? <CircularProgress size={16} /> : <IconRefresh />}
                        onClick={handleRefresh}
                        disabled={refreshing}
                    >
                        Refresh
                    </Button>
                    <Button variant="outlined" startIcon={<IconDownload />}>
                        Export Report
                    </Button>
                </Box>
            </Box>

            {/* Key Metrics */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                        Total Users
                                    </Typography>
                                    <Typography variant="h3">{metrics.totalUsers}</Typography>
                                    <Box display="flex" alignItems="center" mt={1}>
                                        <IconTrendingUp size={16} color={theme.palette.success.main} />
                                        <Typography variant="caption" color="success.main">
                                            +12% from last week
                                        </Typography>
                                    </Box>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
                                    <IconUsers />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                        Active Notebooks
                                    </Typography>
                                    <Typography variant="h3">{metrics.activeNotebooks}</Typography>
                                    <Box display="flex" alignItems="center" mt={1}>
                                        <IconActivity size={16} />
                                        <Typography variant="caption">Running now</Typography>
                                    </Box>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.secondary.light }}>
                                    <IconFileAnalytics />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                        Avg Response Time
                                    </Typography>
                                    <Typography variant="h3">{metrics.avgResponseTime}</Typography>
                                    <Box display="flex" alignItems="center" mt={1}>
                                        <IconCheck size={16} color={theme.palette.success.main} />
                                        <Typography variant="caption" color="success.main">
                                            Optimal
                                        </Typography>
                                    </Box>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.success.light }}>
                                    <IconBolt />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                        Error Rate
                                    </Typography>
                                    <Typography variant="h3">{metrics.errorRate}</Typography>
                                    <Box display="flex" alignItems="center" mt={1}>
                                        <IconTrendingDown size={16} color={theme.palette.success.main} />
                                        <Typography variant="caption" color="success.main">
                                            Low
                                        </Typography>
                                    </Box>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.warning.light }}>
                                    <IconAlertCircle />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Server Status */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Typography variant="h5" gutterBottom>
                                Server Status
                            </Typography>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Server</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Uptime</TableCell>
                                            <TableCell>Users</TableCell>
                                            <TableCell>CPU</TableCell>
                                            <TableCell>Memory</TableCell>
                                            <TableCell>Disk</TableCell>
                                            <TableCell>Network</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {servers.map((server) => (
                                            <TableRow key={server.id}>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <IconServer size={18} />
                                                        <Typography variant="body2">{server.name}</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={server.status}
                                                        size="small"
                                                        icon={
                                                            <Box
                                                                sx={{
                                                                    width: 8,
                                                                    height: 8,
                                                                    borderRadius: '50%',
                                                                    bgcolor: getStatusColor(server.status)
                                                                }}
                                                            />
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>{server.uptime}</TableCell>
                                                <TableCell>
                                                    <Badge badgeContent={server.notebooks} color="primary">
                                                        {server.users}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={server.cpu}
                                                            sx={{ width: 50 }}
                                                            color={server.cpu > 70 ? 'error' : 'primary'}
                                                        />
                                                        <Typography variant="caption">{server.cpu}%</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={server.memory}
                                                            sx={{ width: 50 }}
                                                            color={server.memory > 80 ? 'error' : 'secondary'}
                                                        />
                                                        <Typography variant="caption">{server.memory}%</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={server.disk}
                                                            sx={{ width: 50 }}
                                                            color={server.disk > 85 ? 'error' : 'info'}
                                                        />
                                                        <Typography variant="caption">{server.disk}%</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{server.network}</TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Restart Server">
                                                        <IconButton size="small">
                                                            <IconRefresh size={16} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Server Settings">
                                                        <IconButton size="small">
                                                            <IconSettings size={16} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h5" gutterBottom>
                                System Health
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon>
                                        <IconCheck color={theme.palette.success.main} />
                                    </ListItemIcon>
                                    <ListItemText primary="All servers operational" secondary="No critical issues detected" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <IconAlertCircle color={theme.palette.warning.main} />
                                    </ListItemIcon>
                                    <ListItemText primary="High memory usage" secondary="Server jupyter-hub-02" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <IconCheck color={theme.palette.success.main} />
                                    </ListItemIcon>
                                    <ListItemText primary="Backups up to date" secondary="Last backup: 2 hours ago" />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Typography variant="h5" gutterBottom>
                                Recent Activity
                            </Typography>
                            <List dense>
                                {activityLog.map((activity, index) => (
                                    <ListItem key={index}>
                                        <ListItemIcon>
                                            <Avatar sx={{ width: 28, height: 28, bgcolor: theme.palette.grey[200] }}>
                                                {getActionIcon(activity.type)}
                                            </Avatar>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Box>
                                                    <Typography variant="body2">
                                                        <strong>{activity.user}</strong> {activity.action}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {activity.details}
                                                    </Typography>
                                                </Box>
                                            }
                                            secondary={<Typography variant="caption">{activity.time}</Typography>}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h5" gutterBottom>
                                System Resource Usage
                            </Typography>
                            <ReactApexChart options={systemMetricsOptions} series={systemMetricsSeries} type="area" height={300} />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h5" gutterBottom>
                                User Activity Trends
                            </Typography>
                            <ReactApexChart options={userActivityOptions} series={userActivitySeries} type="bar" height={250} />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h5" gutterBottom>
                                Resource Allocation
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={6}>
                                    <Box textAlign="center">
                                        <CircularProgress
                                            variant="determinate"
                                            value={metrics.avgCpu}
                                            size={100}
                                            thickness={5}
                                            sx={{ mb: 1 }}
                                        />
                                        <Typography variant="h6">{metrics.avgCpu}%</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Average CPU Usage
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box textAlign="center">
                                        <CircularProgress
                                            variant="determinate"
                                            value={metrics.avgMemory}
                                            size={100}
                                            thickness={5}
                                            color="secondary"
                                            sx={{ mb: 1 }}
                                        />
                                        <Typography variant="h6">{metrics.avgMemory}%</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Average Memory Usage
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />
                            <List dense>
                                <ListItem>
                                    <ListItemText primary="Total Storage Used" />
                                    <Typography variant="body2">{metrics.totalStorage}</Typography>
                                </ListItem>
                                <ListItem>
                                    <ListItemText primary="Network Throughput" />
                                    <Typography variant="body2">267 MB/s</Typography>
                                </ListItem>
                                <ListItem>
                                    <ListItemText primary="Requests per Minute" />
                                    <Typography variant="body2">{metrics.requestsPerMin}</Typography>
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ServerMonitoring;
